import { registerPlugin } from "@capacitor/core"
import { LiveActivity } from "capacitor-live-activity"
import { currentPlatformKind } from "./detect"
import { type MobileRunState, parseMobileRunState } from "./mobileRunState"
import type { PlatformKind, PlatformListener, PlatformResult } from "./types"

const LIVE_STATUS_ID = "wildlark-running-work"

export interface LiveStatusSnapshot {
    version: 1
    sequence: number
    computerName: string
    runningCount: number
    attentionCount: number
    updatedAt: string
    topRun?: MobileRunState
}

export interface LiveStatusAvailability {
    supported: boolean
    enabled: boolean
    mode: "activitykit" | "promoted" | "ongoing" | "unavailable"
}

export type LiveStatusOperation = "started" | "updated" | "ended" | "not-running"
export type LiveStatusLifecycle = "active" | "stale" | "pending" | "ended" | "dismissed" | "unknown"

export type LiveStatusTokenEvent = { type: "push-to-start"; token: string } | { type: "activity-update"; activityId: string; token: string }

export interface LiveStatusReconciliation {
    running: boolean
    lifecycle: LiveStatusLifecycle
    activityId?: string
}

export interface LiveStatusAdapter {
    getMonitoringEnabled(): Promise<PlatformResult<boolean>>
    setMonitoringEnabled(enabled: boolean): Promise<PlatformResult<boolean>>
    getTaskTitlesEnabled(): Promise<PlatformResult<boolean>>
    setTaskTitlesEnabled(enabled: boolean): Promise<PlatformResult<boolean>>
    getAvailability(): Promise<PlatformResult<LiveStatusAvailability>>
    start(snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>>
    update(snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>>
    end(snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>>
    reconcile(): Promise<PlatformResult<LiveStatusReconciliation>>
    configureRemoteTokenEndpoint(url: string): Promise<PlatformResult<void>>
    addTokenListener(listener: (event: LiveStatusTokenEvent) => void): Promise<PlatformListener>
    addLifecycleListener(listener: (state: LiveStatusLifecycle) => void): Promise<PlatformListener>
}

interface AndroidLiveStatusPlugin {
    getMonitoringEnabled(): Promise<unknown>
    setMonitoringEnabled(options: { enabled: boolean }): Promise<unknown>
    getTaskTitlesEnabled(): Promise<unknown>
    setTaskTitlesEnabled(options: { enabled: boolean }): Promise<unknown>
    getAvailability(): Promise<unknown>
    start(options: { id: string; contentState: Record<string, string> }): Promise<unknown>
    update(options: { id: string; contentState: Record<string, string> }): Promise<unknown>
    end(options: { id: string; contentState: Record<string, string> }): Promise<unknown>
    reconcile(options: { id: string }): Promise<unknown>
    addListener(eventName: "liveStatusUpdate", listener: (event: unknown) => void): Promise<PlatformListener>
}

const WildLarkLiveStatus = registerPlugin<AndroidLiveStatusPlugin>("WildLarkLiveStatus")

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function failure(error: unknown): PlatformResult<never> {
    return { status: "failed", message: error instanceof Error ? error.message : String(error) }
}

function unavailable<T>(): PlatformResult<T> {
    return { status: "unavailable", reason: "Live status is unavailable on this platform" }
}

function nonEmptyString(value: unknown, field: string): string {
    if (typeof value !== "string" || value.trim().length === 0) throw new Error(`Live status is missing ${field}`)
    return value
}

function nonNegativeInteger(value: unknown, field: string): number {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) throw new Error(`Live status has an invalid ${field}`)
    return value
}

function positiveInteger(value: unknown, field: string): number {
    const result = nonNegativeInteger(value, field)
    if (result < 1) throw new Error(`Live status has an invalid ${field}`)
    return result
}

function isoTimestamp(value: unknown, field: string): string {
    const result = nonEmptyString(value, field)
    if (!Number.isFinite(Date.parse(result))) throw new Error(`Live status has an invalid ${field}`)
    return result
}

export function parseLiveStatusSnapshot(value: unknown): LiveStatusSnapshot {
    if (!isRecord(value)) throw new Error("Live status snapshot must be an object")
    const allowedFields = new Set(["version", "sequence", "computerName", "runningCount", "attentionCount", "updatedAt", "topRun"])
    if (Object.keys(value).some((field) => !allowedFields.has(field))) throw new Error("Live status snapshot contains unsupported fields")
    if (value.version !== 1) throw new Error("Live status snapshot has an unsupported version")
    return {
        version: 1,
        sequence: positiveInteger(value.sequence, "sequence"),
        computerName: nonEmptyString(value.computerName, "computerName"),
        runningCount: nonNegativeInteger(value.runningCount, "runningCount"),
        attentionCount: nonNegativeInteger(value.attentionCount, "attentionCount"),
        updatedAt: isoTimestamp(value.updatedAt, "updatedAt"),
        ...(value.topRun === undefined ? {} : { topRun: parseMobileRunState(value.topRun) }),
    }
}

export function serializeLiveStatusSnapshot(value: LiveStatusSnapshot): Record<string, string> {
    const snapshot = parseLiveStatusSnapshot(value)
    const topRun = snapshot.topRun
    return {
        version: "1",
        sequence: String(snapshot.sequence),
        computerName: snapshot.computerName,
        runningCount: String(snapshot.runningCount),
        attentionCount: String(snapshot.attentionCount),
        title: topRun?.title ?? "WildLark",
        phase: topRun?.phase ?? (snapshot.attentionCount > 0 ? "needs-input" : "succeeded"),
        updatedAt: snapshot.updatedAt,
        ...(topRun?.startedAt ? { startedAt: topRun.startedAt } : {}),
        ...(topRun ? { nodeId: topRun.nodeId, workspaceId: topRun.workspaceId, taskId: topRun.taskId } : {}),
    }
}

function parseLifecycle(value: unknown): LiveStatusLifecycle {
    if (value === "active" || value === "stale" || value === "pending" || value === "ended" || value === "dismissed") return value
    return "unknown"
}

function parseActivityList(value: unknown): LiveStatusReconciliation {
    if (!isRecord(value) || !Array.isArray(value.items)) throw new Error("Live Activity returned an invalid activity list")
    for (const item of value.items) {
        if (!isRecord(item) || item.id !== LIVE_STATUS_ID) continue
        const lifecycle = parseLifecycle(item.state)
        const activityId = nonEmptyString(item.activityId, "activityId")
        return {
            running: lifecycle === "active" || lifecycle === "stale" || lifecycle === "pending",
            lifecycle,
            activityId,
        }
    }
    return { running: false, lifecycle: "unknown" }
}

function parseOperation(value: unknown): LiveStatusOperation {
    if (!isRecord(value)) throw new Error("Live status plugin returned an invalid operation")
    const operation = value.operation
    if (operation === "started" || operation === "updated" || operation === "ended" || operation === "not-running") return operation
    throw new Error("Live status plugin returned an unknown operation")
}

function parseAndroidAvailability(value: unknown): LiveStatusAvailability {
    if (!isRecord(value) || typeof value.supported !== "boolean" || typeof value.enabled !== "boolean") {
        throw new Error("Android live status returned invalid availability")
    }
    const mode = value.mode
    if (mode !== "promoted" && mode !== "ongoing" && mode !== "unavailable") throw new Error("Android live status returned an unknown mode")
    return { supported: value.supported, enabled: value.enabled, mode }
}

function parseMonitoringEnabled(value: unknown): boolean {
    if (!isRecord(value) || typeof value.enabled !== "boolean") throw new Error("Live status returned invalid monitoring state")
    return value.enabled
}

function parseTaskTitlesEnabled(value: unknown): boolean {
    if (!isRecord(value) || typeof value.enabled !== "boolean") throw new Error("Live status returned invalid task-title privacy state")
    return value.enabled
}

function parseAndroidReconciliation(value: unknown): LiveStatusReconciliation {
    if (!isRecord(value) || typeof value.running !== "boolean") throw new Error("Android live status returned invalid reconciliation state")
    return {
        running: value.running,
        lifecycle: parseLifecycle(value.lifecycle),
        ...(typeof value.activityId === "string" && value.activityId ? { activityId: value.activityId } : {}),
    }
}

function parseActivityToken(value: unknown): LiveStatusTokenEvent {
    if (!isRecord(value) || value.id !== LIVE_STATUS_ID) throw new Error("Live Activity returned an invalid update token")
    return {
        type: "activity-update",
        activityId: nonEmptyString(value.activityId, "activityId"),
        token: nonEmptyString(value.token, "token"),
    }
}

function parsePushToStartToken(value: unknown): LiveStatusTokenEvent {
    if (!isRecord(value)) throw new Error("Live Activity returned an invalid push-to-start token")
    return { type: "push-to-start", token: nonEmptyString(value.token, "token") }
}

class UnavailableLiveStatusAdapter implements LiveStatusAdapter {
    async getMonitoringEnabled(): Promise<PlatformResult<boolean>> {
        return unavailable()
    }

    async setMonitoringEnabled(_enabled: boolean): Promise<PlatformResult<boolean>> {
        return unavailable()
    }

    async getTaskTitlesEnabled(): Promise<PlatformResult<boolean>> {
        return unavailable()
    }

    async setTaskTitlesEnabled(_enabled: boolean): Promise<PlatformResult<boolean>> {
        return unavailable()
    }

    async getAvailability(): Promise<PlatformResult<LiveStatusAvailability>> {
        return unavailable()
    }

    async start(_snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>> {
        return unavailable()
    }

    async update(_snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>> {
        return unavailable()
    }

    async end(_snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>> {
        return unavailable()
    }

    async reconcile(): Promise<PlatformResult<LiveStatusReconciliation>> {
        return unavailable()
    }

    async configureRemoteTokenEndpoint(_url: string): Promise<PlatformResult<void>> {
        return unavailable()
    }

    async addTokenListener(_listener: (event: LiveStatusTokenEvent) => void): Promise<PlatformListener> {
        return { remove: async () => undefined }
    }

    async addLifecycleListener(_listener: (state: LiveStatusLifecycle) => void): Promise<PlatformListener> {
        return { remove: async () => undefined }
    }
}

class IOSLiveStatusAdapter implements LiveStatusAdapter {
    async getMonitoringEnabled(): Promise<PlatformResult<boolean>> {
        try {
            return { status: "ok", value: parseMonitoringEnabled(await WildLarkLiveStatus.getMonitoringEnabled()) }
        } catch (error) {
            return failure(error)
        }
    }

    async setMonitoringEnabled(enabled: boolean): Promise<PlatformResult<boolean>> {
        try {
            return { status: "ok", value: parseMonitoringEnabled(await WildLarkLiveStatus.setMonitoringEnabled({ enabled })) }
        } catch (error) {
            return failure(error)
        }
    }

    async getTaskTitlesEnabled(): Promise<PlatformResult<boolean>> {
        try {
            return { status: "ok", value: parseTaskTitlesEnabled(await WildLarkLiveStatus.getTaskTitlesEnabled()) }
        } catch (error) {
            return failure(error)
        }
    }

    async setTaskTitlesEnabled(enabled: boolean): Promise<PlatformResult<boolean>> {
        try {
            return { status: "ok", value: parseTaskTitlesEnabled(await WildLarkLiveStatus.setTaskTitlesEnabled({ enabled })) }
        } catch (error) {
            return failure(error)
        }
    }

    async getAvailability(): Promise<PlatformResult<LiveStatusAvailability>> {
        try {
            const result: unknown = await LiveActivity.isAvailable()
            if (!isRecord(result) || typeof result.value !== "boolean") throw new Error("Live Activity returned invalid availability")
            return { status: "ok", value: { supported: true, enabled: result.value, mode: "activitykit" } }
        } catch (error) {
            return failure(error)
        }
    }

    async start(snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>> {
        try {
            const reconciliation = await this.readReconciliation()
            if (reconciliation.running) return this.update(snapshot)
            const result: unknown = await LiveActivity.startActivityWithPush({
                id: LIVE_STATUS_ID,
                attributes: { version: "1", appName: "WildLark" },
                contentState: serializeLiveStatusSnapshot(snapshot),
                timestamp: Date.parse(snapshot.updatedAt) / 1000,
            })
            if (!isRecord(result)) throw new Error("Live Activity returned an invalid start result")
            nonEmptyString(result.activityId, "activityId")
            return { status: "ok", value: "started" }
        } catch (error) {
            return failure(error)
        }
    }

    async update(snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>> {
        try {
            const reconciliation = await this.readReconciliation()
            if (!reconciliation.running) return { status: "ok", value: "not-running" }
            await LiveActivity.updateActivity({
                id: LIVE_STATUS_ID,
                contentState: serializeLiveStatusSnapshot(snapshot),
                timestamp: Date.parse(snapshot.updatedAt) / 1000,
            })
            return { status: "ok", value: "updated" }
        } catch (error) {
            return failure(error)
        }
    }

    async end(snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>> {
        try {
            const reconciliation = await this.readReconciliation()
            if (!reconciliation.running) return { status: "ok", value: "not-running" }
            await LiveActivity.endActivity({
                id: LIVE_STATUS_ID,
                contentState: serializeLiveStatusSnapshot(snapshot),
                timestamp: Date.parse(snapshot.updatedAt) / 1000,
                dismissalPolicy: "immediate",
            })
            return { status: "ok", value: "ended" }
        } catch (error) {
            return failure(error)
        }
    }

    async reconcile(): Promise<PlatformResult<LiveStatusReconciliation>> {
        try {
            return { status: "ok", value: await this.readReconciliation() }
        } catch (error) {
            return failure(error)
        }
    }

    async configureRemoteTokenEndpoint(url: string): Promise<PlatformResult<void>> {
        try {
            const parsed = new URL(url)
            if (
                parsed.protocol !== "https:" ||
                parsed.username ||
                parsed.password ||
                parsed.search ||
                parsed.hash ||
                !parsed.pathname.startsWith("/v1/activity-tokens/")
            ) {
                throw new Error("Live Activity token endpoint must use the gateway HTTPS route")
            }
            await LiveActivity.setUpdateTokenEndpoint({ url: parsed.toString() })
            return { status: "ok", value: undefined }
        } catch (error) {
            return failure(error)
        }
    }

    async addTokenListener(listener: (event: LiveStatusTokenEvent) => void): Promise<PlatformListener> {
        const [activityToken, pushToStartToken] = await Promise.all([
            LiveActivity.addListener("liveActivityPushToken", (event) => {
                try {
                    listener(parseActivityToken(event))
                } catch {
                    console.warn("[mobile-live-status] Ignored an invalid ActivityKit update token event")
                }
            }),
            LiveActivity.addListener("liveActivityPushToStartToken", (event) => {
                try {
                    listener(parsePushToStartToken(event))
                } catch {
                    console.warn("[mobile-live-status] Ignored an invalid ActivityKit push-to-start token event")
                }
            }),
        ])

        void LiveActivity.getActivityPushTokens({ id: LIVE_STATUS_ID })
            .then((result: unknown) => {
                if (!isRecord(result) || !Array.isArray(result.items)) return
                for (const item of result.items) {
                    try {
                        listener(parseActivityToken(item))
                    } catch {
                        console.warn("[mobile-live-status] Ignored an invalid cached ActivityKit token")
                    }
                }
            })
            .catch(() => undefined)
        void LiveActivity.observePushToStartToken().catch(() => undefined)

        return {
            remove: async () => {
                await Promise.all([activityToken.remove(), pushToStartToken.remove()])
            },
        }
    }

    async addLifecycleListener(listener: (state: LiveStatusLifecycle) => void): Promise<PlatformListener> {
        const handle = await LiveActivity.addListener("liveActivityUpdate", (event) => {
            if (event.id === LIVE_STATUS_ID) listener(parseLifecycle(event.state))
        })
        return { remove: () => handle.remove() }
    }

    private async readReconciliation(): Promise<LiveStatusReconciliation> {
        const result: unknown = await LiveActivity.listActivities()
        return parseActivityList(result)
    }
}

class AndroidLiveStatusAdapter implements LiveStatusAdapter {
    async getMonitoringEnabled(): Promise<PlatformResult<boolean>> {
        try {
            return { status: "ok", value: parseMonitoringEnabled(await WildLarkLiveStatus.getMonitoringEnabled()) }
        } catch (error) {
            return failure(error)
        }
    }

    async setMonitoringEnabled(enabled: boolean): Promise<PlatformResult<boolean>> {
        try {
            return { status: "ok", value: parseMonitoringEnabled(await WildLarkLiveStatus.setMonitoringEnabled({ enabled })) }
        } catch (error) {
            return failure(error)
        }
    }

    async getTaskTitlesEnabled(): Promise<PlatformResult<boolean>> {
        try {
            return { status: "ok", value: parseTaskTitlesEnabled(await WildLarkLiveStatus.getTaskTitlesEnabled()) }
        } catch (error) {
            return failure(error)
        }
    }

    async setTaskTitlesEnabled(enabled: boolean): Promise<PlatformResult<boolean>> {
        try {
            return { status: "ok", value: parseTaskTitlesEnabled(await WildLarkLiveStatus.setTaskTitlesEnabled({ enabled })) }
        } catch (error) {
            return failure(error)
        }
    }

    async getAvailability(): Promise<PlatformResult<LiveStatusAvailability>> {
        try {
            return { status: "ok", value: parseAndroidAvailability(await WildLarkLiveStatus.getAvailability()) }
        } catch (error) {
            return failure(error)
        }
    }

    async start(snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>> {
        return this.operation("start", snapshot)
    }

    async update(snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>> {
        return this.operation("update", snapshot)
    }

    async end(snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>> {
        return this.operation("end", snapshot)
    }

    async reconcile(): Promise<PlatformResult<LiveStatusReconciliation>> {
        try {
            return { status: "ok", value: parseAndroidReconciliation(await WildLarkLiveStatus.reconcile({ id: LIVE_STATUS_ID })) }
        } catch (error) {
            return failure(error)
        }
    }

    async configureRemoteTokenEndpoint(_url: string): Promise<PlatformResult<void>> {
        return unavailable()
    }

    async addTokenListener(_listener: (event: LiveStatusTokenEvent) => void): Promise<PlatformListener> {
        return { remove: async () => undefined }
    }

    async addLifecycleListener(listener: (state: LiveStatusLifecycle) => void): Promise<PlatformListener> {
        const handle = await WildLarkLiveStatus.addListener("liveStatusUpdate", (event) => {
            if (isRecord(event) && event.id === LIVE_STATUS_ID) listener(parseLifecycle(event.lifecycle))
        })
        return handle
    }

    private async operation(method: "start" | "update" | "end", snapshot: LiveStatusSnapshot): Promise<PlatformResult<LiveStatusOperation>> {
        try {
            const options = { id: LIVE_STATUS_ID, contentState: serializeLiveStatusSnapshot(snapshot) }
            const result = await WildLarkLiveStatus[method](options)
            return { status: "ok", value: parseOperation(result) }
        } catch (error) {
            return failure(error)
        }
    }
}

let cached: { kind: PlatformKind; adapter: LiveStatusAdapter } | null = null

export function getLiveStatusAdapter(kind: PlatformKind = currentPlatformKind()): LiveStatusAdapter {
    if (cached?.kind === kind) return cached.adapter
    const adapter = kind === "ios" ? new IOSLiveStatusAdapter() : kind === "android" ? new AndroidLiveStatusAdapter() : new UnavailableLiveStatusAdapter()
    cached = { kind, adapter }
    return adapter
}

export function resetLiveStatusAdapterForTests(): void {
    cached = null
}
